/**
 * CwPanelGroup — a titled group of search results (one provider/entity bucket).
 *
 * Sprint CW-G TKT-1. Renders a header (provider + entity-type label,
 * count) and up to `limit` CwResultRow children. Used by the global
 * search dropdown in Panels mode.
 */
import { type FC, type MouseEvent } from 'react';
import CwResultRow, { type CwSearchHit } from './CwResultRow';

interface Props {
  title: string;
  subtitle?: string;
  hits: CwSearchHit[];
  limit?: number;
  activeId?: string | null;
  /** Prefix the parent uses to derive stable option IDs for ARIA. */
  idPrefix: string;
  onSelect: (hit: CwSearchHit, event: MouseEvent) => void;
  onHoverEnter?: (hit: CwSearchHit, e: { clientX: number; clientY: number }) => void;
  onHoverMove?: (e: { clientX: number; clientY: number }) => void;
  onHoverLeave?: () => void;
  /** Opens this provider in Connected Search, focused. When set, the
   * header and the "more" footer become links to it. */
  onOpenProvider?: () => void;
}

const CwPanelGroup: FC<Props> = ({
  title,
  subtitle,
  hits,
  limit = 5,
  activeId,
  idPrefix,
  onSelect,
  onHoverEnter,
  onHoverMove,
  onHoverLeave,
  onOpenProvider,
}) => {
  if (hits.length === 0) return null;
  const visible = hits.slice(0, limit);
  const overflow = Math.max(0, hits.length - visible.length);

  return (
    <section className="cw-panel-group" aria-label={title}>
      <header className="cw-panel-group__header">
        {onOpenProvider ? (
          <button
            type="button"
            className="cw-panel-group__open"
            onClick={onOpenProvider}
            title={`Open ${subtitle ?? title} in Connected Search`}
          >
            <span className="cw-panel-group__title">{title}</span>
            {subtitle && <span className="cw-panel-group__subtitle">{subtitle}</span>}
          </button>
        ) : (
          <>
            <span className="cw-panel-group__title">{title}</span>
            {subtitle && <span className="cw-panel-group__subtitle">{subtitle}</span>}
          </>
        )}
        <span className="cw-panel-group__count">{hits.length}</span>
        <span className="cw-panel-group__col" aria-hidden="true">Modified</span>
      </header>
      <ul className="cw-panel-group__list" role="presentation">
        {visible.map((hit) => {
          const optionId = `${idPrefix}-${hit.id}`;
          return (
            <li key={hit.id} role="presentation">
              <CwResultRow
                hit={hit}
                id={optionId}
                active={activeId === optionId}
                onSelect={onSelect}
                onHoverEnter={onHoverEnter}
                onHoverMove={onHoverMove}
                onHoverLeave={onHoverLeave}
              />
            </li>
          );
        })}
      </ul>
      {overflow > 0 && (
        <footer className="cw-panel-group__overflow">
          {onOpenProvider ? (
            <button
              type="button"
              className="cw-panel-group__more"
              onClick={onOpenProvider}
              aria-label={`${overflow} more in ${subtitle ?? title}: open in Connected Search`}
            >
              + {overflow} more
            </button>
          ) : (
            <>+ {overflow} more</>
          )}
        </footer>
      )}
    </section>
  );
};

export default CwPanelGroup;
